/** SOW templates — save a scope's categories+items as a reusable template and
 * apply it to another deal (v1 scopeSaveTemplate / scopeApplyTemplate, "add"
 * mode). DEVICE-LOCAL (IndexedDB), same call v2 makes for saved views: v1's
 * SCOPE_TPLS is an org-wide settings blob and the v2 write door only whitelists
 * the AGENT settings key, so org-wide templates need a future settings-door
 * change (PHASE17). Local keeps Daniel's reusable scopes on his machine now,
 * with zero migration risk right before go-live. Applying a template writes
 * real per-record cat/item ops through the existing door, so the applied scope
 * is fully shared (only the template LIBRARY is local). */
import { idbGet, idbSet } from "../../lib/idb";
import type { RecOp } from "../../lib/outbox";
import { opSowDelCat, opSowDelItem, opSowPutCat, opSowPutItem, sowId, sowSeedMeta, type SowCat, type SowItem, type SowState } from "./api";

const KEY = "sow_templates_v1";

/** An item as stored in a template — the SowItem fields minus the per-scope
 * identity (id/catId/order are re-minted on apply). `raw` rides along so linked
 * materials (raw.mats) come with the template. */
export type SowTemplateItem = Omit<SowItem, "id" | "catId" | "order">;
export interface SowTemplateCat {
  name: string;
  section: string;
  items: SowTemplateItem[];
}
export interface SowTemplate {
  id: string;
  name: string;
  createdAt: number;
  cats: SowTemplateCat[];
}

export async function loadTemplates(): Promise<SowTemplate[]> {
  const v = await idbGet<SowTemplate[]>(KEY);
  return Array.isArray(v) ? v : [];
}
export async function saveTemplates(list: SowTemplate[]): Promise<void> {
  await idbSet(KEY, list);
}

const tnum = (v: unknown): number => {
  const f = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(f) ? f : 0;
};
const tstr = (v: unknown): string => (typeof v === "string" ? v : "");

/** Parse v1's org-wide SCOPE_TPLS blob into apply-ready SowTemplates. v1 shape:
 * [{ name, type, payload:{ cats:[{ name, section, items:[{qty,desc,name,unit,
 * labor,matName,combined,matPrice,material,useCombined}] }] } }]. READ-ONLY —
 * v2 never writes SCOPE_TPLS (that would need v1's exact save format; deferred),
 * so v1's template list can't be disturbed. Field map: v1 `desc` → v2 `notes`
 * (v2's description field); the whole original item rides in `raw`, so on apply
 * opSowPutItem preserves v1's desc/matName/matPrice AND writes v2's notes — the
 * applied scope reads correctly in BOTH apps. Empty categories are kept (they're
 * intentional placeholders in Daniel's template). */
export function parseSharedTemplates(raw: unknown): SowTemplate[] {
  if (!Array.isArray(raw)) return [];
  const out: SowTemplate[] = [];
  raw.forEach((entry, i) => {
    const e = entry && typeof entry === "object" ? (entry as Record<string, unknown>) : {};
    const name = tstr(e["name"]).trim() || `Template ${i + 1}`;
    const payload = e["payload"] && typeof e["payload"] === "object" ? (e["payload"] as Record<string, unknown>) : {};
    const rawCats = Array.isArray(payload["cats"]) ? (payload["cats"] as unknown[]) : Array.isArray(e["cats"]) ? (e["cats"] as unknown[]) : [];
    const cats: SowTemplateCat[] = [];
    for (const rc of rawCats) {
      const c = rc && typeof rc === "object" ? (rc as Record<string, unknown>) : {};
      const cname = tstr(c["name"]).trim();
      if (!cname) continue;
      const section = tstr(c["section"]) || "interiors";
      const rawItems = Array.isArray(c["items"]) ? (c["items"] as unknown[]) : [];
      const items: SowTemplateItem[] = [];
      for (const ri of rawItems) {
        const it = ri && typeof ri === "object" ? (ri as Record<string, unknown>) : {};
        const iname = tstr(it["name"]).trim();
        if (!iname) continue;
        items.push({
          name: iname,
          qty: tnum(it["qty"]),
          unit: tstr(it["unit"]) || "each",
          labor: tnum(it["labor"]),
          material: tnum(it["material"]),
          combined: tnum(it["combined"]),
          useCombined: it["useCombined"] === true,
          desc: tstr(it["desc"]) || tstr(it["notes"]), // v1 desc (legacy v2 notes fallback)
          laborNotes: tstr(it["laborNotes"]),
          materialNotes: tstr(it["materialNotes"]),
          photos: Array.isArray(it["photos"]) ? (it["photos"] as unknown[]).filter((p): p is string => typeof p === "string") : [],
          markupMode: "inherit",
          markupPct: 0,
          isSub: it["isSub"] === true,
          raw: it, // preserve v1's original (matName/matPrice/mats) for round-trip
        });
      }
      cats.push({ name: cname, section, items });
    }
    out.push({ id: "v1tpl-" + i, name, createdAt: 0, cats });
  });
  return out;
}

// ---- org-wide templates (Phase 28, parity item #2 2026-07-23) ---------------
// SCOPE_TPLS becomes door-writable (MIGRATION_CRM2_PHASE28_SCOPE_TPLS.sql), so
// v2 now WRITES the same org blob v1 reads/edits. Everything below works on
// the RAW v1-shaped array — parse only for display; write in v1's exact
// scopeTplSave() shape so v1's template modal keeps working untouched.

const rawObj = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

/** Serialize a v2 template into v1's exact SCOPE_TPLS entry shape
 * (scope-of-work.js scopeTplSave): {name, type, payload:{cats:[{name, section,
 * items:[{name, desc, qty, unit, labor, material, useCombined, combined,
 * matName, matPrice, laborNotes, materialNotes, matUrl, matImg, mats}]}]}}.
 * Items born in v1 ride their original extras through `raw`; v2-born items
 * get v1-safe defaults. `photos` is v2-only surplus — v1 ignores unknown keys
 * and parseSharedTemplates round-trips it. */
export function templateToV1Entry(tpl: SowTemplate, type = "full"): Record<string, unknown> {
  return {
    name: tpl.name,
    type,
    payload: {
      cats: tpl.cats.map((c) => ({
        name: c.name,
        section: c.section,
        items: c.items.map((it) => {
          const r = rawObj(it.raw);
          return {
            name: it.name,
            desc: it.desc,
            qty: it.qty,
            unit: it.unit,
            labor: it.labor,
            material: it.material,
            useCombined: it.useCombined,
            combined: it.combined,
            matName: tstr(r["matName"]),
            matPrice: tnum(r["matPrice"]),
            laborNotes: it.laborNotes || "",
            materialNotes: it.materialNotes || "",
            matUrl: tstr(r["matUrl"]),
            matImg: tstr(r["matImg"]),
            mats: Array.isArray(r["mats"]) ? (JSON.parse(JSON.stringify(r["mats"])) as unknown[]) : [],
            photos: it.photos,
          };
        }),
      })),
    },
  };
}

/** Raw-array surgery for the org SCOPE_TPLS blob. parseSharedTemplates ids are
 * "v1tpl-<index>", so a shared template maps back to its raw slot by index. */
export const sharedTplIndex = (id: string): number => {
  const m = /^v1tpl-(\d+)$/.exec(id);
  return m ? Number(m[1]) : -1;
};

export function scopeTplsAppend(raw: unknown, entry: Record<string, unknown>): unknown[] {
  const list = Array.isArray(raw) ? [...raw] : [];
  // same-name replace, mirroring v2's local save semantics (no silent dupes)
  const name = tstr(entry["name"]);
  const without = list.filter((e) => tstr(rawObj(e)["name"]) !== name);
  return [...without, entry];
}

export function scopeTplsRename(raw: unknown, index: number, name: string): unknown[] {
  const list = Array.isArray(raw) ? [...raw] : [];
  if (index < 0 || index >= list.length) return list;
  list[index] = { ...rawObj(list[index]), name: name.trim().slice(0, 120) };
  return list;
}

export function scopeTplsDelete(raw: unknown, index: number): unknown[] {
  const list = Array.isArray(raw) ? [...raw] : [];
  if (index < 0 || index >= list.length) return list;
  list.splice(index, 1);
  return list;
}

/** Replace one raw entry wholesale (the item-level editor's save — v1
 * scopeTplEdRender edits payload.cats in place and saves the whole entry). */
export function scopeTplsUpdateEntry(raw: unknown, index: number, entry: Record<string, unknown>): unknown[] {
  const list = Array.isArray(raw) ? [...raw] : [];
  if (index < 0 || index >= list.length) return list;
  list[index] = entry;
  return list;
}

/** Deep-copy a raw entry for editing (plain JSON data by construction). */
export function cloneTplEntry(raw: unknown, index: number): Record<string, unknown> | null {
  const list = Array.isArray(raw) ? raw : [];
  const e = list[index];
  if (e === null || typeof e !== "object") return null;
  const copy = JSON.parse(JSON.stringify(e)) as Record<string, unknown>;
  const payload = rawObj(copy["payload"]);
  if (!Array.isArray(payload["cats"])) payload["cats"] = [];
  copy["payload"] = payload;
  return copy;
}

/** Build a template from the current scope: every category with items, each
 * item stripped of its per-scope identity. Empty categories are skipped.
 * Pass `catIds` to save only a SUBSET of categories (partial-scope template);
 * omit it (or pass undefined) to include the whole scope as before. */
export function scopeToTemplate(name: string, sow: SowState, nowMs: number, catIds?: Set<string>): SowTemplate {
  const cats: SowTemplateCat[] = [];
  for (const c of [...sow.cats].sort((a, b) => a.order - b.order)) {
    if (catIds && !catIds.has(c.id)) continue; // partial-scope: skip unselected categories
    const items = sow.items
      .filter((i) => i.catId === c.id)
      .sort((a, b) => a.order - b.order)
      .map(({ id: _id, catId: _catId, order: _order, ...rest }) => rest);
    if (items.length) cats.push({ name: c.name, section: c.section, items });
  }
  return { id: sowId("sc") + sowId("si"), name: name.trim().slice(0, 120), createdAt: nowMs, cats };
}

// ---- Settings template EDITOR bridge (2026-07-23) ---------------------------
// The Settings › Templates library edits a template with the SAME full SOW
// editor used on a deal (material picker, copy, rename, every field), not a
// stripped-down form. To do that it needs a live SowState to hand the editor,
// and a way to fold the edited state back into the org SCOPE_TPLS array. These
// two are exact inverses, built from the helpers above so the round-trip keeps
// v1 fidelity (matName/matPrice/mats included).

/** One raw SCOPE_TPLS entry -> a live SowState the full SOW editor can edit in
 * memory (fresh per-scope ids; empty categories preserved). */
export function templateEntryToSowState(entry: unknown): SowState {
  const tpl = parseSharedTemplates([entry])[0] ?? { id: "", name: "", createdAt: 0, cats: [] };
  const empty: SowState = { meta: sowSeedMeta(), cats: [], items: [] };
  return applyTemplateOps("__tpl__", tpl, empty, "add").next;
}

/** The edited SowState -> a v1-shaped SCOPE_TPLS entry (name preserved). Inverse
 * of templateEntryToSowState; `nowMs` stamps the template's createdAt. */
export function sowStateToTemplateEntry(
  name: string, sow: SowState, nowMs: number, type = "full",
): Record<string, unknown> {
  return templateToV1Entry(scopeToTemplate(name, sow, nowMs), type);
}

/** Apply a template to a scope (v1 scopeApplyTemplate). Three modes:
 *   - "add" (default): every template category becomes a NEW category (fresh
 *     ids) with its items, appended. Keeps existing callers/behavior intact.
 *   - "merge": for each template cat, reuse an existing scope cat with the same
 *     name+section (appending only items whose name isn't already in that cat);
 *     if none exists, create it like "add".
 *   - "replace": first delete existing scope cats whose name matches a template
 *     cat name (and every item in them — emitting del ops), then add fresh.
 * Returns the next state + the door ops to persist it. */
export function applyTemplateOps(
  dealKey: string,
  tpl: SowTemplate,
  sow: SowState,
  mode: "add" | "merge" | "replace" = "add",
): { next: SowState; ops: RecOp[] } {
  const ops: RecOp[] = [];
  let cats = [...sow.cats];
  let items = [...sow.items];

  // replace: clear out any existing cat (and its items) sharing a template name.
  if (mode === "replace") {
    const names = new Set(tpl.cats.map((c) => c.name));
    const doomed = cats.filter((c) => names.has(c.name));
    if (doomed.length) {
      const doomedIds = new Set(doomed.map((c) => c.id));
      for (const it of items) if (doomedIds.has(it.catId)) ops.push(opSowDelItem(dealKey, it.id));
      for (const c of doomed) ops.push(opSowDelCat(dealKey, c.id));
      items = items.filter((i) => !doomedIds.has(i.catId));
      cats = cats.filter((c) => !doomedIds.has(c.id));
    }
  }

  for (const tc of tpl.cats) {
    // merge reuses a same-name+section cat; add/replace always make a new one.
    let cat = mode === "merge" ? cats.find((c) => c.name === tc.name && c.section === tc.section) : undefined;
    if (!cat) {
      const order = cats.filter((c) => c.section === tc.section).length;
      cat = { id: sowId("sc"), name: tc.name, section: tc.section, order, followUp: false, note: "" };
      cats.push(cat);
      ops.push(opSowPutCat(dealKey, cat));
    }
    const catId = cat.id;
    for (const ti of tc.items) {
      // merge skips items whose name already lives in the target cat.
      if (mode === "merge" && items.some((i) => i.catId === catId && i.name === ti.name)) continue;
      const it: SowItem = { ...ti, id: sowId("si"), catId, order: items.filter((i) => i.catId === catId).length };
      items.push(it);
      ops.push(opSowPutItem(dealKey, it));
    }
  }
  return { next: { ...sow, cats, items }, ops };
}
